import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch, useSelector } from 'react-redux';

import { buscarProductosConfiguracion, limpiarProductosConfiguracion } from '../../../actions/configuracion';
import { formatNumber } from '../../../utils/generalFunctions';

import SeleccionProveedorModal from './SeleccionProveedorModal';

const formatMoney = (v) => formatNumber(Number(v || 0), 2, '$', '');

const buildRowKey = (productoId, unidadId) =>
    `${productoId}-${unidadId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const TablaProductosPaquete = ({ items, setItems, disabled = false }) => {
    const dispatch = useDispatch();

    const { listado: productosBusqueda } = useSelector(state => state.configuracion?.productos || { listado: [] });

    const [codigoTienda, setCodigoTienda] = useState('');
    const [codigoProveedor, setCodigoProveedor] = useState('');
    const [nombreBusqueda, setNombreBusqueda] = useState('');
    const [buscandoProductos, setBuscandoProductos] = useState(false);
    const [productoPendiente, setProductoPendiente] = useState(null);
    const debounceRef = useRef(null);

    const algunFiltro = !!(
        (codigoTienda || '').trim() ||
        (codigoProveedor || '').trim() ||
        (nombreBusqueda || '').trim()
    );

    useEffect(() => {
        if (disabled) return;

        const ct = (codigoTienda || '').trim();
        const cp = (codigoProveedor || '').trim();
        const n = (nombreBusqueda || '').trim();

        if (!ct && !cp && !n) {
            dispatch(limpiarProductosConfiguracion());
            setBuscandoProductos(false);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        setBuscandoProductos(true);

        debounceRef.current = setTimeout(async () => {
            await dispatch(buscarProductosConfiguracion({
                nombre: n || null,
                codigo: ct || null,
                codigo_proveedor: cp || null
            }));
            setBuscandoProductos(false);
        }, 450);

        return () => clearTimeout(debounceRef.current);
    }, [codigoTienda, codigoProveedor, nombreBusqueda, disabled, dispatch]);

    const opcionesBusqueda = useMemo(() => (
        (Array.isArray(productosBusqueda) ? productosBusqueda : []).map((p) => {
            const cod = p?.codigo_tienda ? ` · ${p.codigo_tienda}` : '';
            return {
                value: p?._id || null,
                label: `${p?.nombre || ''}${cod}`,
                raw: p
            };
        })
    ), [productosBusqueda]);

    const onSeleccionarProducto = (opt) => {
        const p = opt?.raw;
        if (!p) return;

        setProductoPendiente(p);
    };

    const onConfirmarProveedor = ({ precio, precioCompra }) => {
        const p = productoPendiente;
        if (!p) return;

        const unidades = Array.isArray(p.unidades) ? p.unidades : [];
        const primeraUnidad = unidades[0] || null;

        setItems((prev) => ([
            ...prev,
            {
                key: buildRowKey(p._id, primeraUnidad?.unidad?._id || 'sin-unidad'),
                productoId: p._id,
                productoNombre: p.nombre,
                codigoTienda: p.codigo_tienda || '',
                unidades,
                unidadId: primeraUnidad?.unidad?._id || null,
                unidadNombre: primeraUnidad?.unidad?.nombre || null,
                conversion: Number(primeraUnidad?.cantidad || 1),
                cantidad: 1,
                precio: Number(precio || 0),
                precioCompra: Number(precioCompra || 0)
            }
        ]));

        setProductoPendiente(null);
    };

    const onChangeUnidad = (key, opt) => {
        setItems((prev) => prev.map((it) => {
            if (it.key !== key) return it;
            const found = (it.unidades || []).find((u) => u?.unidad?._id === opt?.value);
            return {
                ...it,
                unidadId: opt?.value || null,
                unidadNombre: opt?.label || null,
                conversion: Number(found?.cantidad || 1)
            };
        }));
    };

    const onChangeCampo = (key, campo, valor) => {
        setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [campo]: valor } : it)));
    };

    const eliminarFila = (key) => {
        setItems((prev) => prev.filter((it) => it.key !== key));
    };

    const totalReferencia = items.reduce(
        (acc, it) => acc + (Number(it.precio || 0) * Number(it.cantidad || 0)),
        0
    );

    return (
        <div>
            {!disabled && (
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-semibold" style={{ fontSize: 13 }}>Buscar productos</div>
                        {buscandoProductos ? <span className="text-muted" style={{ fontSize: 12 }}>Buscando...</span> : null}
                    </div>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <div className="text-muted mb-1" style={{ fontSize: 12 }}>Código tienda</div>
                            <Form.Control
                                type="text"
                                placeholder="Ej. IN018"
                                value={codigoTienda}
                                onChange={(e) => setCodigoTienda(e.target.value)}
                            />
                        </div>

                        <div className="col-md-3">
                            <div className="text-muted mb-1" style={{ fontSize: 12 }}>Código proveedor</div>
                            <Form.Control
                                type="text"
                                placeholder="Ej. TBA-033"
                                value={codigoProveedor}
                                onChange={(e) => setCodigoProveedor(e.target.value)}
                            />
                        </div>

                        <div className="col-md-6">
                            <div className="text-muted mb-1" style={{ fontSize: 12 }}>Nombre</div>
                            <Form.Control
                                type="text"
                                placeholder="Nombre del producto"
                                value={nombreBusqueda}
                                onChange={(e) => setNombreBusqueda(e.target.value)}
                            />
                        </div>

                        <div className="col-md-12">
                            <div className="text-muted mb-1" style={{ fontSize: 12 }}>Producto</div>
                            <Select
                                placeholder="Selecciona un producto de los resultados..."
                                value={null}
                                onChange={onSeleccionarProducto}
                                options={opcionesBusqueda}
                                isLoading={buscandoProductos}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                noOptionsMessage={() =>
                                    buscandoProductos
                                        ? 'Buscando...'
                                        : (algunFiltro ? 'Sin resultados' : 'Aplica un filtro para buscar')
                                }
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="table-responsive">
                <Table bordered hover className="mb-0">
                    <thead>
                        <tr>
                            <th>Producto</th>
                            <th style={{ width: 150 }}>Unidad</th>
                            <th style={{ width: 110 }} className="text-center">Cantidad</th>
                            <th style={{ width: 160 }} className="text-end">Precio compra</th>
                            <th style={{ width: 160 }} className="text-end">Precio</th>
                            <th style={{ width: 160 }} className="text-end">Importe</th>
                            {!disabled && <th style={{ width: 60 }} />}
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={disabled ? 6 : 7} className="text-center text-muted">
                                    Sin productos agregados
                                </td>
                            </tr>
                        )}

                        {items.map((it) => {
                            const unidadesOptions = (it.unidades || []).map((u) => ({
                                value: u?.unidad?._id || null,
                                label: u?.unidad?.nombre || ''
                            }));
                            const unidadValue = it.unidadId ? { value: it.unidadId, label: it.unidadNombre } : null;
                            const importe = Number(it.precio || 0) * Number(it.cantidad || 0);

                            return (
                                <tr key={it.key}>
                                    <td>
                                        <div className="fw-semibold">{it.productoNombre}</div>
                                        {it.codigoTienda && (
                                            <div className="text-muted" style={{ fontSize: 12 }}>{it.codigoTienda}</div>
                                        )}
                                    </td>
                                    <td>
                                        {unidadesOptions.length > 0 ? (
                                            <Select
                                                isDisabled={disabled}
                                                value={unidadValue}
                                                options={unidadesOptions}
                                                onChange={(opt) => onChangeUnidad(it.key, opt)}
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        ) : (
                                            <span className="text-muted">Sin unidad</span>
                                        )}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control text-center"
                                            value={it.cantidad}
                                            min={0}
                                            disabled={disabled}
                                            onChange={(e) => onChangeCampo(
                                                it.key,
                                                'cantidad',
                                                e.target.value === '' ? '' : Number(e.target.value)
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <CurrencyInput
                                            value={it.precioCompra}
                                            decimalsLimit={2}
                                            allowNegativeValue={false}
                                            className="form-control text-end"
                                            prefix="$ "
                                            groupSeparator=","
                                            decimalSeparator="."
                                            disabled={disabled}
                                            onValueChange={(value) => onChangeCampo(
                                                it.key,
                                                'precioCompra',
                                                value === undefined || value === null || value === '' ? 0 : Number(value)
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <CurrencyInput
                                            value={it.precio}
                                            decimalsLimit={2}
                                            allowNegativeValue={false}
                                            className="form-control text-end"
                                            prefix="$ "
                                            groupSeparator=","
                                            decimalSeparator="."
                                            disabled={disabled}
                                            onValueChange={(value) => onChangeCampo(
                                                it.key,
                                                'precio',
                                                value === undefined || value === null || value === '' ? 0 : Number(value)
                                            )}
                                        />
                                    </td>
                                    <td className="text-end align-middle">{formatMoney(importe)}</td>
                                    {!disabled && (
                                        <td className="text-center align-middle">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link text-danger"
                                                onClick={() => eliminarFila(it.key)}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                    {items.length > 0 && (
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="text-end fw-semibold">Total referencia</td>
                                <td className="text-end fw-semibold">{formatMoney(totalReferencia)}</td>
                                {!disabled && <td />}
                            </tr>
                        </tfoot>
                    )}
                </Table>
            </div>

            <SeleccionProveedorModal
                show={!!productoPendiente}
                onHide={() => setProductoPendiente(null)}
                producto={productoPendiente}
                onConfirm={onConfirmarProveedor}
            />
        </div>
    );
};

export default TablaProductosPaquete;
